import { Navigate, Route, Routes, useLocation } from 'react-router';
import Layout from './components/Layout.jsx';
import { Spinner } from './components/ui.jsx';
import { useAuth, homeFor } from './state/AuthContext.jsx';

import Home from './pages/Home.jsx';
import Search from './pages/Search.jsx';
import PropertyDetail from './pages/PropertyDetail.jsx';
import LandlordProfile from './pages/LandlordProfile.jsx';
import HowItWorks from './pages/HowItWorks.jsx';
import Login from './pages/Login.jsx';
import Register from './pages/Register.jsx';
import ForgotPassword from './pages/ForgotPassword.jsx';
import ResetPassword from './pages/ResetPassword.jsx';
import TrackApplication from './pages/TrackApplication.jsx';
import Profile from './pages/Profile.jsx';
import SetPassword from './pages/SetPassword.jsx';
import PrintableReceipt from './pages/PrintableReceipt.jsx';
import PrintableLease from './pages/PrintableLease.jsx';
import PrintableFullReceipt from './pages/PrintableFullReceipt.jsx';
import PrintableUtilityReceipt from './pages/PrintableUtilityReceipt.jsx';
import PrintableSaleAgreement from './pages/PrintableSaleAgreement.jsx';
import MyUtilities from './pages/MyUtilities.jsx';
import MyOffers from './pages/MyOffers.jsx';
import LandRegistry from './pages/land/LandRegistry.jsx';
import ParcelPage from './pages/land/ParcelPage.jsx';
import MyLand from './pages/land/MyLand.jsx';
import RegisterPlot from './pages/land/RegisterPlot.jsx';
import Messages from './pages/Messages.jsx';
import NotFound from './pages/NotFound.jsx';

import TenantDashboard from './pages/tenant/TenantDashboard.jsx';
import TenantApplications from './pages/tenant/TenantApplications.jsx';
import SavedListings from './pages/tenant/SavedListings.jsx';
import MyLease from './pages/tenant/MyLease.jsx';
import MyRent from './pages/tenant/MyRent.jsx';

import LandlordDashboard from './pages/landlord/LandlordDashboard.jsx';
import LandlordProperties from './pages/landlord/LandlordProperties.jsx';
import PropertyEditor from './pages/landlord/PropertyEditor.jsx';
import LandlordApplications from './pages/landlord/LandlordApplications.jsx';
import UnitManager from './pages/landlord/UnitManager.jsx';
import TenantDirectory from './pages/landlord/TenantDirectory.jsx';
import LeaseList from './pages/landlord/LeaseList.jsx';
import LeaseEditor from './pages/landlord/LeaseEditor.jsx';
import LeaseDetail from './pages/landlord/LeaseDetail.jsx';
import RentRoll from './pages/landlord/RentRoll.jsx';
import Utilities from './pages/landlord/Utilities.jsx';
import UtilityService from './pages/landlord/UtilityService.jsx';
import UtilityAccount from './pages/landlord/UtilityAccount.jsx';
import UtilityTasks from './pages/landlord/UtilityTasks.jsx';
import MonthlyBilling from './pages/landlord/MonthlyBilling.jsx';
import Team from './pages/landlord/Team.jsx';
import ManagerActivity from './pages/landlord/ManagerActivity.jsx';
import Announcements from './pages/landlord/Announcements.jsx';
import PaymentAccounts from './pages/landlord/PaymentAccounts.jsx';
import Offers from './pages/landlord/Offers.jsx';

import AdminOverview from './pages/admin/AdminOverview.jsx';
import AdminListings from './pages/admin/AdminListings.jsx';
import AdminUsers from './pages/admin/AdminUsers.jsx';
import AdminReports from './pages/admin/AdminReports.jsx';
import AdminSettings from './pages/admin/AdminSettings.jsx';
import AdminLand from './pages/admin/AdminLand.jsx';

// Gate for the role dashboards. While the session is still being checked we hold
// the route rather than bouncing a signed-in user to the login page.
//
// `orLease` opens a route to anybody who is the tenant on a lease, whatever
// their account says they are. Being on a lease is a fact about the lease, so an
// account set up under the wrong role keeps the pages about its own tenancy.
const RequireRole = ({ roles, orLease = false, children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <Spinner label="Checking your session" />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;

  // An account still on a landlord-issued temporary password can go exactly one
  // place. The API enforces the same rule, so this is convenience, not security.
  if (user.mustChangePassword && location.pathname !== '/set-password') {
    return <Navigate to="/set-password" replace />;
  }

  const allowed = !roles || roles.includes(user.role) || (orLease && user.hasLease);
  if (!allowed) return <Navigate to={homeFor(user)} replace />;

  return children;
};

const App = () => (
  <Routes>
    <Route element={<Layout />}>
      <Route index element={<Home />} />
      <Route path="/search" element={<Search />} />
      <Route path="/properties/:id" element={<PropertyDetail />} />
      <Route path="/landlords/:id" element={<LandlordProfile />} />
      <Route path="/how-it-works" element={<HowItWorks />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      {/* Open on purpose: somebody who applied as a guest has no account to sign
          into until a landlord approves them. */}
      <Route path="/track" element={<TrackApplication />} />
      <Route path="/land" element={<LandRegistry />} />
      <Route path="/land/:id" element={<ParcelPage />} />

      <Route
        path="/profile"
        element={
          <RequireRole>
            <Profile />
          </RequireRole>
        }
      />
      <Route
        path="/messages"
        element={
          <RequireRole>
            <Messages />
          </RequireRole>
        }
      />
      <Route
        path="/set-password"
        element={
          <RequireRole>
            <SetPassword />
          </RequireRole>
        }
      />
      <Route
        path="/receipts/:id"
        element={
          <RequireRole>
            <PrintableReceipt />
          </RequireRole>
        }
      />
      <Route
        path="/leases/:id/print"
        element={
          <RequireRole>
            <PrintableLease />
          </RequireRole>
        }
      />
      <Route
        path="/offers/:id/agreement"
        element={
          <RequireRole>
            <PrintableSaleAgreement />
          </RequireRole>
        }
      />
      <Route
        path="/leases/:id/full-receipt"
        element={
          <RequireRole>
            <PrintableFullReceipt />
          </RequireRole>
        }
      />
      {/* No role: a utility customer may be a tenant of this landlord or a
          neighbour who rents from nobody, and both need their own receipt. */}
      <Route
        path="/utility-receipts/:id"
        element={
          <RequireRole>
            <PrintableUtilityReceipt />
          </RequireRole>
        }
      />
      <Route
        path="/my/utilities"
        element={
          <RequireRole>
            <MyUtilities />
          </RequireRole>
        }
      />
      <Route
        path="/my/offers"
        element={
          <RequireRole>
            <MyOffers />
          </RequireRole>
        }
      />
      <Route
        path="/my/land"
        element={
          <RequireRole>
            <MyLand />
          </RequireRole>
        }
      />
      <Route
        path="/my/land/new"
        element={
          <RequireRole roles={['landlord', 'manager']}>
            <RegisterPlot />
          </RequireRole>
        }
      />
      <Route
        path="/my/land/:id"
        element={
          <RequireRole roles={['landlord', 'manager']}>
            <RegisterPlot />
          </RequireRole>
        }
      />

      <Route
        path="/tenant"
        element={
          <RequireRole roles={['tenant']}>
            <TenantDashboard />
          </RequireRole>
        }
      />
      <Route
        path="/tenant/applications"
        element={
          <RequireRole roles={['tenant']}>
            <TenantApplications />
          </RequireRole>
        }
      />
      <Route
        path="/tenant/saved"
        element={
          <RequireRole roles={['tenant']}>
            <SavedListings />
          </RequireRole>
        }
      />
      {/* orLease: somebody on a lease keeps these two whatever their role says,
          so a mistaken role never hides the agreement they live under. */}
      <Route
        path="/tenant/lease"
        element={
          <RequireRole roles={['tenant']} orLease>
            <MyLease />
          </RequireRole>
        }
      />
      <Route
        path="/tenant/rent"
        element={
          <RequireRole roles={['tenant']} orLease>
            <MyRent />
          </RequireRole>
        }
      />

      <Route
        path="/landlord"
        element={
          <RequireRole roles={['landlord', 'manager']}>
            <LandlordDashboard />
          </RequireRole>
        }
      />
      <Route
        path="/landlord/properties"
        element={
          <RequireRole roles={['landlord', 'manager']}>
            <LandlordProperties />
          </RequireRole>
        }
      />
      <Route
        path="/landlord/properties/new"
        element={
          <RequireRole roles={['landlord', 'manager']}>
            <PropertyEditor />
          </RequireRole>
        }
      />
      <Route
        path="/landlord/properties/:id/edit"
        element={
          <RequireRole roles={['landlord', 'manager']}>
            <PropertyEditor />
          </RequireRole>
        }
      />
      <Route
        path="/landlord/applications"
        element={
          <RequireRole roles={['landlord', 'manager']}>
            <LandlordApplications />
          </RequireRole>
        }
      />
      <Route
        path="/landlord/applications/:id"
        element={
          <RequireRole roles={['landlord', 'manager']}>
            <LandlordApplications />
          </RequireRole>
        }
      />
      <Route
        path="/landlord/properties/:id/units"
        element={
          <RequireRole roles={['landlord', 'manager']}>
            <UnitManager />
          </RequireRole>
        }
      />
      <Route
        path="/landlord/tenants"
        element={
          <RequireRole roles={['landlord', 'manager']}>
            <TenantDirectory />
          </RequireRole>
        }
      />
      <Route
        path="/landlord/leases"
        element={
          <RequireRole roles={['landlord', 'manager']}>
            <LeaseList />
          </RequireRole>
        }
      />
      <Route
        path="/landlord/leases/new"
        element={
          <RequireRole roles={['landlord', 'manager']}>
            <LeaseEditor />
          </RequireRole>
        }
      />
      <Route
        path="/landlord/leases/:id"
        element={
          <RequireRole roles={['landlord', 'manager']}>
            <LeaseDetail />
          </RequireRole>
        }
      />
      <Route
        path="/landlord/rent"
        element={
          <RequireRole roles={['landlord', 'manager']}>
            <RentRoll />
          </RequireRole>
        }
      />
      {/* Declared before /landlord/utilities/:id so "tasks" is not read as a
          service id. */}
      <Route
        path="/landlord/utilities"
        element={
          <RequireRole roles={['landlord', 'manager']}>
            <Utilities />
          </RequireRole>
        }
      />
      <Route
        path="/landlord/utilities/tasks"
        element={
          <RequireRole roles={['landlord', 'manager']}>
            <UtilityTasks />
          </RequireRole>
        }
      />
      <Route
        path="/landlord/utilities/accounts/:id"
        element={
          <RequireRole roles={['landlord', 'manager']}>
            <UtilityAccount />
          </RequireRole>
        }
      />
      <Route
        path="/landlord/utilities/:id/monthly"
        element={
          <RequireRole roles={['landlord', 'manager']}>
            <MonthlyBilling />
          </RequireRole>
        }
      />
      <Route
        path="/landlord/utilities/:id"
        element={
          <RequireRole roles={['landlord', 'manager']}>
            <UtilityService />
          </RequireRole>
        }
      />
      <Route
        path="/landlord/rent/:id"
        element={
          <RequireRole roles={['landlord', 'manager']}>
            <LeaseDetail />
          </RequireRole>
        }
      />
      <Route
        path="/landlord/offers"
        element={
          <RequireRole roles={['landlord', 'manager']}>
            <Offers />
          </RequireRole>
        }
      />
      <Route
        path="/landlord/notices"
        element={
          <RequireRole roles={['landlord', 'manager']}>
            <Announcements />
          </RequireRole>
        }
      />
      <Route
        path="/landlord/team"
        element={
          <RequireRole roles={['landlord']}>
            <Team />
          </RequireRole>
        }
      />
      <Route
        path="/landlord/team/activity"
        element={
          <RequireRole roles={['landlord']}>
            <ManagerActivity />
          </RequireRole>
        }
      />
      <Route
        path="/landlord/team/:id/activity"
        element={
          <RequireRole roles={['landlord']}>
            <ManagerActivity />
          </RequireRole>
        }
      />
      <Route
        path="/landlord/payment-accounts"
        element={
          <RequireRole roles={['landlord']}>
            <PaymentAccounts />
          </RequireRole>
        }
      />

      <Route
        path="/admin"
        element={
          <RequireRole roles={['admin']}>
            <AdminOverview />
          </RequireRole>
        }
      />
      <Route
        path="/admin/listings"
        element={
          <RequireRole roles={['admin']}>
            <AdminListings />
          </RequireRole>
        }
      />
      <Route
        path="/admin/users"
        element={
          <RequireRole roles={['admin']}>
            <AdminUsers />
          </RequireRole>
        }
      />
      <Route
        path="/admin/reports"
        element={
          <RequireRole roles={['admin']}>
            <AdminReports />
          </RequireRole>
        }
      />
      <Route
        path="/admin/land"
        element={
          <RequireRole roles={['admin']}>
            <AdminLand />
          </RequireRole>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <RequireRole roles={['admin']}>
            <AdminSettings />
          </RequireRole>
        }
      />

      <Route path="*" element={<NotFound />} />
    </Route>
  </Routes>
);

export default App;
